/**
 * Voice Recognition Service using Web Speech API
 *
 * Provides voice input functionality for quick task capture
 * with real-time transcription and language detection
 */

// Types for Web Speech API (not fully typed in TypeScript)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

// Get the SpeechRecognition constructor (vendor-prefixed in some browsers)
type SpeechRecognitionConstructor = new () => {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

const SpeechRecognition: SpeechRecognitionConstructor | undefined =
  (window as unknown as { SpeechRecognition?: SpeechRecognitionConstructor }).SpeechRecognition ||
  (window as unknown as { webkitSpeechRecognition?: SpeechRecognitionConstructor }).webkitSpeechRecognition;

// Check if speech recognition is supported
export function isSpeechRecognitionSupported(): boolean {
  return !!SpeechRecognition;
}

// Voice recognition state
export type VoiceState =
  | 'idle'
  | 'listening'
  | 'processing'
  | 'error';

// Voice recognition result
export interface VoiceResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

// Voice recognition error
export interface VoiceError {
  code: string;
  message: string;
}

// Event callbacks
export interface VoiceRecognitionCallbacks {
  onStart?: () => void;
  onResult?: (result: VoiceResult) => void;
  onError?: (error: VoiceError) => void;
  onEnd?: () => void;
  onStateChange?: (state: VoiceState) => void;
}

// Recognition instance type
type RecognitionInstance = InstanceType<NonNullable<typeof SpeechRecognition>>;
let recognition: RecognitionInstance | null = null;
let currentCallbacks: VoiceRecognitionCallbacks = {};
let currentState: VoiceState = 'idle';

// Update state and notify
function setState(state: VoiceState): void {
  currentState = state;
  currentCallbacks.onStateChange?.(state);
}

/**
 * Get the current voice recognition state
 */
export function getVoiceState(): VoiceState {
  return currentState;
}

/**
 * Initialize the speech recognition engine
 */
export function initVoiceRecognition(
  options: {
    language?: string;
    continuous?: boolean;
    interimResults?: boolean;
    maxAlternatives?: number;
  } = {}
): boolean {
  if (!isSpeechRecognitionSupported()) {
    console.error('Speech recognition not supported');
    return false;
  }

  const {
    language = navigator.language || 'en-US',
    continuous = false,
    interimResults = true,
    maxAlternatives = 1,
  } = options;

  // SpeechRecognition is guaranteed to be defined here due to isSpeechRecognitionSupported check
  recognition = new SpeechRecognition!();
  recognition.lang = language;
  recognition.continuous = continuous;
  recognition.interimResults = interimResults;
  recognition.maxAlternatives = maxAlternatives;

  // Handle recognition start
  recognition.onstart = () => {
    setState('listening');
    currentCallbacks.onStart?.();
  };

  // Handle results
  recognition.onresult = (event: SpeechRecognitionEvent) => {
    const result = event.results[event.resultIndex];
    if (!result) return;

    const alternative = result[0];
    if (!alternative) return;

    currentCallbacks.onResult?.({
      transcript: alternative.transcript,
      confidence: alternative.confidence,
      isFinal: result.isFinal,
    });

    // If final result, update state
    if (result.isFinal) {
      setState('processing');
    }
  };

  // Handle errors
  recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
    setState('error');

    const errorMessages: Record<string, string> = {
      'no-speech': 'No speech was detected. Please try again.',
      'audio-capture': 'No microphone was found.',
      'not-allowed': 'Microphone permission was denied.',
      'network': 'Network error occurred.',
      'aborted': 'Voice recognition was aborted.',
      'language-not-supported': 'Language is not supported.',
      'service-not-allowed': 'Voice recognition service is not allowed.',
    };

    currentCallbacks.onError?.({
      code: event.error,
      message: errorMessages[event.error] || 'An error occurred.',
    });
  };

  // Handle end of recognition
  recognition.onend = () => {
    if (currentState !== 'error') {
      setState('idle');
    }
    currentCallbacks.onEnd?.();
  };

  return true;
}

/**
 * Start voice recognition
 */
export function startListening(callbacks: VoiceRecognitionCallbacks = {}): boolean {
  if (!recognition) {
    const initialized = initVoiceRecognition();
    if (!initialized) return false;
  }

  if (currentState === 'listening') {
    console.warn('Already listening');
    return false;
  }

  currentCallbacks = callbacks;

  try {
    recognition!.start();
    return true;
  } catch (error) {
    console.error('Failed to start voice recognition:', error);
    setState('error');
    callbacks.onError?.({
      code: 'start-error',
      message: 'Failed to start voice recognition',
    });
    return false;
  }
}

/**
 * Stop voice recognition
 */
export function stopListening(): void {
  if (recognition && currentState === 'listening') {
    recognition.stop();
  }
}

/**
 * Abort voice recognition (cancels without final result)
 */
export function abortListening(): void {
  if (recognition && currentState === 'listening') {
    recognition.abort();
    setState('idle');
  }
}

/**
 * Change the recognition language
 */
export function setLanguage(language: string): void {
  if (recognition) {
    recognition.lang = language;
  }
}

// ============================================================================
// React Hook for Voice Recognition
// ============================================================================

import { useState, useCallback, useEffect, useRef } from 'react';

export interface UseVoiceRecognitionOptions {
  language?: string;
  continuous?: boolean;
  autoStart?: boolean;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
}

export interface UseVoiceRecognitionResult {
  transcript: string;
  interimTranscript: string;
  isListening: boolean;
  state: VoiceState;
  error: VoiceError | null;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  isSupported: boolean;
}

export function useVoiceRecognition(
  options: UseVoiceRecognitionOptions = {}
): UseVoiceRecognitionResult {
  const {
    language = navigator.language || 'en-US',
    continuous = false,
    autoStart = false,
    onTranscript,
  } = options;

  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [state, setState] = useState<VoiceState>('idle');
  const [error, setError] = useState<VoiceError | null>(null);

  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const isSupported = isSpeechRecognitionSupported();

  // Initialize recognition engine
  useEffect(() => {
    if (isSupported) {
      initVoiceRecognition({ language, continuous });
    }

    return () => {
      abortListening();
    };
  }, [language, continuous, isSupported]);

  // Auto-start if enabled
  useEffect(() => {
    if (autoStart && isSupported) {
      startListeningHandler();
    }
  }, [autoStart, isSupported]);

  const startListeningHandler = useCallback(() => {
    if (!isSupported) return;

    setError(null);
    setInterimTranscript('');

    startListening({
      onStateChange: (newState) => {
        setState(newState);
      },
      onResult: (result) => {
        if (result.isFinal) {
          setTranscript((prev) => {
            const newTranscript = prev
              ? `${prev} ${result.transcript}`
              : result.transcript;
            onTranscriptRef.current?.(result.transcript, true);
            return newTranscript;
          });
          setInterimTranscript('');
        } else {
          setInterimTranscript(result.transcript);
          onTranscriptRef.current?.(result.transcript, false);
        }
      },
      onError: (err) => {
        setError(err);
        setState('error');
      },
    });
  }, [isSupported]);

  const stopListeningHandler = useCallback(() => {
    stopListening();
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  return {
    transcript,
    interimTranscript,
    isListening: state === 'listening',
    state,
    error,
    startListening: startListeningHandler,
    stopListening: stopListeningHandler,
    resetTranscript,
    isSupported,
  };
}

// ============================================================================
// Supported Languages
// ============================================================================

export const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'fr-FR', name: 'French' },
  { code: 'de-DE', name: 'German' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
] as const;
